import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Download, FileSpreadsheet, Loader2, Mail, Network, Sparkles, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { cadastroInteligenteService } from '@/services/api';
import { ResultadoCadastroInteligente, ResumoConvites } from '@/types';
import { baixarModeloCadastroInteligente } from '@/lib/cadastroInteligenteModelo';

/** Quantos nomes de departamento/cargo aparecem antes do "e mais N". */
const MAX_NOMES = 8;

const listaCurta = (nomes: string[]) =>
  nomes.length > MAX_NOMES ? `${nomes.slice(0, MAX_NOMES).join(', ')} e mais ${nomes.length - MAX_NOMES}` : nomes.join(', ');

/** Linha do resumo (prévia e resultado). */
function ItemResumo({ rotulo, valor, detalhe }: { rotulo: string; valor: number; detalhe?: string }) {
  return (
    <div className="rounded-md border border-border/60 p-3">
      <p className="text-2xl font-semibold">{valor}</p>
      <p className="text-sm text-muted-foreground">{rotulo}</p>
      {detalhe && <p className="mt-1 break-words text-xs text-muted-foreground">{detalhe}</p>}
    </div>
  );
}

function Resumo({ r }: { r: ResultadoCadastroInteligente }) {
  const verbo = r.simulacao ? 'a criar' : 'criadas';
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      <ItemResumo rotulo={`Pessoas ${verbo}`} valor={r.pessoas_criadas} />
      {r.pessoas_vinculadas > 0 && (
        <ItemResumo rotulo={r.simulacao ? 'Contas a vincular' : 'Contas vinculadas'} valor={r.pessoas_vinculadas} detalhe="Já usavam o Kora em outra empresa" />
      )}
      <ItemResumo rotulo={r.simulacao ? 'Departamentos novos' : 'Departamentos criados'} valor={r.departamentos_criados.length} detalhe={listaCurta(r.departamentos_criados)} />
      <ItemResumo rotulo={r.simulacao ? 'Cargos novos' : 'Cargos criados'} valor={r.cargos_criados.length} detalhe={listaCurta(r.cargos_criados)} />
      <ItemResumo rotulo="Gestores definidos" valor={r.gestores_definidos} />
      <ItemResumo
        rotulo={r.simulacao ? 'Convites a registrar' : 'Convites registrados'}
        valor={r.convites}
        detalhe={r.simulacao ? 'E-mail para a pessoa definir a senha' : `${r.convites_a_enviar} em envio agora`}
      />
    </div>
  );
}

/** Sem e-mail configurado, ninguém recebe o convite: avisa antes de confirmar e no resultado. */
function AvisoEmailDesligado({ r }: { r: ResultadoCadastroInteligente }) {
  if (r.envio_email_configurado || r.convites === 0) return null;
  return (
    <Alert>
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>
        O envio de e-mail não está configurado: as contas são criadas, mas ninguém recebe o convite para definir a senha.
        Depois que o e-mail for configurado, cada pessoa pode usar "Esqueci minha senha" na tela de login.
      </AlertDescription>
    </Alert>
  );
}

/** Convites que ficaram para depois (opção desmarcada, limite do dia, falha ou servidor reiniciado no envio). */
function ConvitesPendentes({ resumo, enviando, onEnviar }: { resumo: ResumoConvites; enviando: boolean; onEnviar: () => void }) {
  const naFila = resumo.pendentes + resumo.falharam + resumo.enviando;
  if (naFila === 0) return null;
  const motivo = !resumo.envio_email_configurado
    ? 'O envio de e-mail não está configurado.'
    : resumo.disponiveis_hoje === 0
      ? `O limite de ${resumo.limite_dia} convites por dia já foi atingido hoje; tente amanhã.`
      : null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Mail className="h-5 w-5 text-primary" />
          Convites para enviar
        </CardTitle>
        <CardDescription>
          {resumo.enviando ? `${resumo.enviando} saindo agora, ` : ''}{resumo.pendentes} pendente(s)
          {resumo.falharam ? ` e ${resumo.falharam} que falharam` : ''}. Hoje ainda dá para enviar {resumo.disponiveis_hoje}{' '}
          (limite de {resumo.limite_dia} por dia).
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{motivo ?? 'Cada pessoa recebe um link para criar a própria senha.'}</p>
        <Button onClick={onEnviar} disabled={enviando || !!motivo || resumo.pendentes + resumo.falharam === 0}>
          {enviando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
          Enviar convites
        </Button>
      </CardContent>
    </Card>
  );
}

function ListaErros({ erros }: { erros: ResultadoCadastroInteligente['erros'] }) {
  if (erros.length === 0) return null;
  return (
    <div className="space-y-2">
      <p className="flex items-center gap-1.5 text-sm font-medium text-amber-600 dark:text-amber-500">
        <AlertCircle className="h-4 w-4" />
        {erros.length} aviso(s) por linha
      </p>
      <div className="max-h-72 overflow-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-muted">
            <tr>
              <th className="w-20 px-3 py-2 text-left font-medium">Linha</th>
              <th className="px-3 py-2 text-left font-medium">O que aconteceu</th>
            </tr>
          </thead>
          <tbody>
            {erros.map((e, i) => (
              <tr key={`${e.linha}-${i}`} className="border-t">
                <td className="px-3 py-2 tabular-nums">{e.linha}</td>
                <td className="px-3 py-2">{e.motivo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Cadastro inteligente (docs/cadastro-inteligente-2026-11-01.md): uma planilha com uma linha por pessoa cria pessoas,
 * departamentos e cargos, já com gestor. Mostra uma prévia (nada gravado) antes de confirmar; no fim, oferece gerar
 * o organograma.
 */
export default function CadastroInteligente() {
  const { user, empresaAtiva, empresaFiltroId } = useAuth();
  const { isModuleEnabled } = usePermissions();
  const navigate = useNavigate();
  const isSuperAdmin = user?.tipo === 'superadmin';
  const empresaId = isSuperAdmin ? (empresaFiltroId && empresaFiltroId !== 'all' ? empresaFiltroId : null) : empresaAtiva?.id ?? null;

  const inputArquivo = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<ResultadoCadastroInteligente | null>(null);
  const [resultado, setResultado] = useState<ResultadoCadastroInteligente | null>(null);
  const [processando, setProcessando] = useState<'previa' | 'importar' | null>(null);
  // Convites só saem se o admin marcar (desmarcado por padrão: protege o e-mail do Kora contra envio em massa).
  const [enviarConvites, setEnviarConvites] = useState(false);
  const [convites, setConvites] = useState<ResumoConvites | null>(null);
  const [enviandoConvites, setEnviandoConvites] = useState(false);

  const carregarConvites = useCallback(async () => {
    if (!empresaId) return setConvites(null);
    const resposta = await cadastroInteligenteService.convites(isSuperAdmin ? empresaId : null);
    setConvites(resposta.success ? resposta.data : null);
  }, [empresaId, isSuperAdmin]);

  useEffect(() => {
    void carregarConvites();
  }, [carregarConvites]);

  // Enquanto convites estão saindo (ou logo depois de mandar enviar, antes de o servidor reservá-los), atualiza o card
  // a cada 3 s: o admin vê o progresso e as falhas sem recarregar a página.
  const [acompanharAte, setAcompanharAte] = useState(0);
  useEffect(() => {
    if (!convites || (convites.enviando === 0 && Date.now() > acompanharAte)) return;
    const id = window.setTimeout(() => void carregarConvites(), 3000);
    return () => window.clearTimeout(id);
  }, [convites, acompanharAte, carregarConvites]);

  if (user?.tipo !== 'admin' && !isSuperAdmin) return <Navigate to="/dashboard" replace />;

  const recomecar = () => {
    setArquivo(null);
    setPrevia(null);
    setResultado(null);
    if (inputArquivo.current) inputArquivo.current.value = '';
  };

  const escolherArquivo = async (escolhido: File | undefined) => {
    if (!escolhido) return;
    setPrevia(null);
    setResultado(null);
    setProcessando('previa');
    // Cópia em memória do arquivo como estava na prévia: o "Confirmar" envia exatamente o que foi revisado, mesmo que a
    // planilha seja editada e salva no Excel depois (o navegador nem consegue reler um arquivo alterado no disco).
    let file: File;
    try {
      file = new File([await escolhido.arrayBuffer()], escolhido.name, { type: escolhido.type });
    } catch {
      setProcessando(null);
      toast.error('Não foi possível ler o arquivo. Se ele estiver aberto no Excel, salve, feche e escolha de novo.');
      recomecar();
      return;
    }
    setArquivo(file);
    const resposta = await cadastroInteligenteService.previa(file, isSuperAdmin ? empresaId : null);
    setProcessando(null);
    if (!resposta.success || !resposta.data) {
      toast.error(resposta.error || 'Não foi possível ler a planilha');
      recomecar();
      return;
    }
    setPrevia(resposta.data);
  };

  const confirmar = async () => {
    if (!arquivo || processando) return;
    setProcessando('importar');
    const resposta = await cadastroInteligenteService.importar(arquivo, isSuperAdmin ? empresaId : null, enviarConvites);
    setProcessando(null);
    if (!resposta.success || !resposta.data) {
      toast.error(resposta.error || 'Não foi possível importar a planilha');
      return;
    }
    setResultado(resposta.data);
    setPrevia(null);
    setEnviarConvites(false);
    toast.success('Cadastro concluído');
    if (resposta.data.convites_a_enviar) setAcompanharAte(Date.now() + 15000);
    void carregarConvites();
  };

  const enviarPendentes = async () => {
    setEnviandoConvites(true);
    const resposta = await cadastroInteligenteService.enviarConvites(isSuperAdmin ? empresaId : null);
    setEnviandoConvites(false);
    if (!resposta.success || !resposta.data) {
      toast.error(resposta.error || 'Não foi possível enviar os convites');
      return;
    }
    const { a_enviar: aEnviar, ...resumo } = resposta.data;
    setConvites(resumo);
    if (aEnviar) setAcompanharAte(Date.now() + 15000);
    toast.success(aEnviar ? `${aEnviar} convite(s) em envio` : 'Nenhum convite pôde ser enviado agora');
  };

  const nadaACriar = !!previa && previa.pessoas_criadas + previa.pessoas_vinculadas === 0;
  const podeGerarOrganograma = isModuleEnabled('organograma') || isSuperAdmin;

  return (
    <AppLayout>
      <div className="mx-auto max-w-4xl space-y-6 animate-fade-in">
        <div className="rounded-lg border bg-card p-4">
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Sparkles className="h-6 w-6 text-primary" />
            Cadastro inteligente
          </h1>
          <p className="text-muted-foreground">
            Cadastre a equipe inteira de uma vez: pessoas, departamentos, cargos e gestores numa planilha só. Depois, gere o
            organograma em um clique.
          </p>
        </div>

        {isSuperAdmin && !empresaId && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Selecione uma empresa no seletor do cabeçalho para importar.</AlertDescription>
          </Alert>
        )}

        {!resultado && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Envie a planilha da equipe</CardTitle>
              <CardDescription>
                Uma linha por pessoa: nome, e-mail, departamento, cargo, e-mail do gestor e perfil. O que não existir é criado.
                Antes de gravar, você vê uma prévia.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => void baixarModeloCadastroInteligente()}>
                <Download className="mr-2 h-4 w-4" />
                Baixar modelo Excel
              </Button>
              <input
                ref={inputArquivo}
                type="file"
                accept=".xlsx,.csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  // Limpa o campo: escolher de novo o mesmo arquivo (corrigido no Excel) precisa disparar outra prévia.
                  e.target.value = '';
                  void escolherArquivo(file);
                }}
              />
              <Button
                data-tour="cadastro-inteligente"
                onClick={() => inputArquivo.current?.click()}
                disabled={!empresaId || !!processando}
              >
                {processando === 'previa' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                {arquivo ? 'Escolher outra planilha' : 'Enviar planilha'}
              </Button>
              {arquivo && (
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <FileSpreadsheet className="h-4 w-4" />
                  {arquivo.name}
                </span>
              )}
            </CardContent>
          </Card>
        )}

        {previa && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Prévia: nada foi gravado ainda</CardTitle>
              <CardDescription>
                {previa.total} linha(s) lida(s). Confira o que será criado e os avisos; corrija a planilha e envie de novo, se
                precisar.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Resumo r={previa} />
              <AvisoEmailDesligado r={previa} />
              <ListaErros erros={previa.erros} />
              {previa.erros.length > 0 && !nadaACriar && (
                <p className="text-sm text-muted-foreground">
                  Ao confirmar, as linhas com erro ficam de fora e as demais são gravadas.
                </p>
              )}
              {previa.convites > 0 && (
                <div className="flex items-start gap-3 rounded-md border border-border/60 p-3">
                  <Checkbox
                    id="enviar_convites"
                    checked={enviarConvites}
                    onCheckedChange={(v) => setEnviarConvites(v === true)}
                    disabled={!previa.envio_email_configurado || previa.convites_disponiveis_hoje === 0}
                    className="mt-0.5"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="enviar_convites" className="cursor-pointer">
                      Enviar agora o convite por e-mail para as {previa.convites} pessoa(s) nova(s)
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Limite de {previa.limite_convites_dia} convites por dia por empresa; hoje ainda dá para enviar{' '}
                      {previa.convites_disponiveis_hoje}. Sem marcar (ou acima do limite), os convites ficam pendentes e podem
                      ser enviados depois nesta tela.
                    </p>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" onClick={recomecar} disabled={!!processando}>
                  Cancelar
                </Button>
                <Button onClick={() => void confirmar()} disabled={nadaACriar || !!processando}>
                  {processando === 'importar' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Confirmar cadastro
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {convites && (
          <ConvitesPendentes resumo={convites} enviando={enviandoConvites} onEnviar={() => void enviarPendentes()} />
        )}

        {resultado && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Equipe cadastrada
              </CardTitle>
              <CardDescription>
                {resultado.convites_a_enviar > 0
                  ? `${resultado.convites_a_enviar} pessoa(s) vão receber um e-mail para definir a própria senha (link válido por 7 dias).`
                  : 'Pronto. Confira os cadastros nas telas de Usuários, Departamentos e Cargos.'}
                {resultado.convites > resultado.convites_a_enviar &&
                  ` ${resultado.convites - resultado.convites_a_enviar} convite(s) ficaram pendentes: envie em "Convites para enviar".`}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Resumo r={resultado} />
              <AvisoEmailDesligado r={resultado} />
              <ListaErros erros={resultado.erros} />
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" onClick={recomecar}>
                  Importar outra planilha
                </Button>
                <Button variant="outline" onClick={() => navigate('/usuarios')}>
                  Ver usuários
                </Button>
                {podeGerarOrganograma && (
                  <Button onClick={() => navigate('/organograma?gerar=1')}>
                    <Network className="mr-2 h-4 w-4" />
                    Gerar organograma agora
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
